// Utility functions for the admin section.
import { formatINR, formatDate as formatDisplayDate, formatDateTime } from '../../../utils/format';

/** Money in rupees: formatCurrency(2499) -> "₹2,499". Other currencies still work when passed explicitly. */
export const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'INR') return formatINR(amount);
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(value);
};

/** 'short' -> "4 Oct 2026", 'datetime' -> "4 Oct 2026, 3:42 pm", 'iso' -> ISO string. */
export const formatDate = (date, format = 'short') => {
  if (format === 'datetime') return formatDateTime(date);
  if (format === 'iso') {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
  }
  return formatDisplayDate(date);
};

export const formatNumber = number => new Intl.NumberFormat('en-IN').format(Number(number) || 0);

export const truncateText = (text, maxLength = 100) => {
  const value = String(text ?? '');
  if (value.length <= maxLength) return value;
  return `${value.substring(0, maxLength).trimEnd()}…`;
};

export const getInitials = name =>
  String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);

export const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email ?? ''));

/** An absolute http(s) URL with a host that has a dot. */
export const isValidURL = url => {
  try {
    const parsed = new URL(String(url ?? '').trim());
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.hostname.includes('.');
  } catch {
    return false;
  }
};

/** Quotes a CSV cell when it holds a comma, quote or line break; inner quotes are doubled. */
const csvCell = value => {
  if (value === null || value === undefined) return '';
  const text = value instanceof Date ? value.toISOString() : typeof value === 'object' ? JSON.stringify(value) : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Downloads rows (objects with the same keys) as a CSV file that opens cleanly in Excel. */
export const exportToCSV = (data, filename = 'export.csv') => {
  if (!Array.isArray(data) || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const lines = [headers.map(csvCell).join(','), ...data.map(row => headers.map(h => csvCell(row[h])).join(','))];

  // The BOM makes Excel read the file as UTF-8 (₹, accented names).
  const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const calculatePercentage = (value, total) => {
  if (!total) return 0;
  return ((value / total) * 100).toFixed(1);
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const sortByKey = (array, key, order = 'asc') =>
  [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal === bVal) return 0;
    const result = aVal > bVal ? 1 : -1;
    return order === 'asc' ? result : -result;
  });

export const filterBySearch = (array, searchTerm, keys) => {
  if (!searchTerm) return array;
  const lowerSearch = searchTerm.toLowerCase();
  return array.filter(item => keys.some(key => String(item[key] ?? '').toLowerCase().includes(lowerSearch)));
};

export const groupBy = (array, key) =>
  array.reduce((result, item) => {
    const group = item[key];
    if (!result[group]) result[group] = [];
    result[group].push(item);
    return result;
  }, {});

export const daysAgo = date => {
  const past = new Date(date);
  if (Number.isNaN(past.getTime())) return '—';
  const diffDays = Math.floor(Math.abs(Date.now() - past.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

/** Digits only, for wa.me and tel: links ("+91 70331-61175" -> "917033161175"). */
export const phoneDigits = phone => String(phone ?? '').replace(/\D/g, '');

/** Guess an image MIME type from a URL's extension (defaults to JPEG). */
export const guessImageMime = url => {
  const path = String(url ?? '').split(/[?#]/)[0].toLowerCase();
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.gif')) return 'image/gif';
  if (path.endsWith('.svg')) return 'image/svg+xml';
  if (path.endsWith('.avif')) return 'image/avif';
  return 'image/jpeg';
};

const helpers = {
  formatCurrency,
  formatDate,
  formatNumber,
  truncateText,
  getInitials,
  isValidEmail,
  isValidURL,
  exportToCSV,
  calculatePercentage,
  debounce,
  sortByKey,
  filterBySearch,
  groupBy,
  daysAgo,
  phoneDigits,
  guessImageMime,
};

export default helpers;
